import { memo, useCallback } from "react";
import ModalWrapper from "..";
import { Button } from "../../button";
import { useTranslation } from "react-i18next";
import toast, { Toaster } from "react-hot-toast";
import {
  useCancelIssuanceRenewalIdForStaffFromContractedCompanies,
  useCancelLostStickerIDCardRequest,
  useCancelOfficeAccessAuthorization,
  useCancelParkingEntrySticker,
  useCancelPcInOutPermitById,
  useCancelPermitToWorkById,
  useCancelWarehouseInOutPermitById,
} from "../../../hooks/permit/mutation";
import { API_ERRORS, PermitTypes } from "../../../constants/common";

interface CancelPermitModalProps {
  selectedPermitId: number;
  permitTypeId: number;
  setSelectedPermitId: React.Dispatch<React.SetStateAction<number>>;
  isOpen: boolean;
  onOpenChange: () => void;
}

const CancelPermitModal = ({
  selectedPermitId,
  setSelectedPermitId,
  isOpen,
  onOpenChange,
  permitTypeId,
}: CancelPermitModalProps) => {
  const {
    mutateAsync: CancelPcInOutPermitById,
    isPending: isCancelPcInOutPermitByIdLoading,
  } = useCancelPcInOutPermitById();

  const {
    mutateAsync: CancelWarehouseInOutPermitById,
    isPending: isCancelWarehouseInOutPermitByIdLoading,
  } = useCancelWarehouseInOutPermitById();

  const {
    mutateAsync: CancelPermitToWorkById,
    isPending: isCancelPermitToWorkByIdLoading,
  } = useCancelPermitToWorkById();

  const { mutateAsync: CancelLostStickerIDCardRequest, isPending: isCancelLostStickerIDCardRequestLoading } = useCancelLostStickerIDCardRequest();

  const { mutateAsync: CancelParkingEntrySticker, isPending: isCancelParkingEntryStickerLoading } = useCancelParkingEntrySticker();

  const { mutateAsync: CancelOfficeAccessAuthorization, isPending: isCancelOfficeAccessAuthorizationLoading } = useCancelOfficeAccessAuthorization();

  const { mutateAsync: CancelIssuanceRenewalIdForStaffFromContractedCompanies, isPending: isCancelIssuanceRenewalIdForStaffFromContractedCompaniesLoading } = useCancelIssuanceRenewalIdForStaffFromContractedCompanies();

  const { t } = useTranslation();

  const handleCancelPermit = useCallback(async () => {
    if (selectedPermitId === 0) return;

    try {
      const body = {
        encryptedId: selectedPermitId.toString(),
      };

      if (permitTypeId === PermitTypes.PC_IN_OUT_PERMIT) {
        await CancelPcInOutPermitById(body);
      } else if (permitTypeId === PermitTypes.WAREHOUSE_IN_OUT_PERMIT) {
        await CancelWarehouseInOutPermitById(body);
      } else if (permitTypeId === PermitTypes.PERMIT_TO_WORK) {
        await CancelPermitToWorkById(body);
      } else if (permitTypeId === PermitTypes.LOST_VEHICLE_STICKER_ID_CARD) {
        await CancelLostStickerIDCardRequest(body);
      } else if (permitTypeId === PermitTypes.PARKING_STICKER_REQUEST) {
        await CancelParkingEntrySticker(body);
      } else if (permitTypeId === PermitTypes.OFFICE_ACCESS_AUTHORIZATION) {
        await CancelOfficeAccessAuthorization(body);
      } else if (permitTypeId === PermitTypes.ISSUANCE_RENEWAL_ID) {
        await CancelIssuanceRenewalIdForStaffFromContractedCompanies(body);
      }

      toast.success(t("permitCancelledSuccessfully"));
      onOpenChange();
      setSelectedPermitId(0);
    } catch (error: any) {
      const errorCode = error.errors && Object.keys(error.errors)[0];

      if (errorCode === API_ERRORS.CANNOT_CANCEL_OLD_PERMIT_REQUESTS) {
        toast.error(t("cannotCancelOldPermitRequests"));
      } else if (errorCode === API_ERRORS.PERMIT_ALREADY_CANCELLED) {
        toast.error(t("permitAlreadyCancelled"));
      } else {
        toast.error(t("somethingWentWrong"));
      }
    }
  }, [
    selectedPermitId,
    permitTypeId,
    t,
    onOpenChange,
    setSelectedPermitId,
    CancelPcInOutPermitById,
    CancelWarehouseInOutPermitById,
    CancelPermitToWorkById,
    CancelLostStickerIDCardRequest,
    CancelParkingEntrySticker,
    CancelOfficeAccessAuthorization,
    CancelIssuanceRenewalIdForStaffFromContractedCompanies,
  ]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <ModalWrapper isOpen={isOpen} onOpenChange={onOpenChange}>
        <div className="flex flex-col gap-6 justify-center items-center">
          <p className="text-lg md:text-xl font-medium">
            {t("sureToCancelPermit")}
          </p>

          <div className="flex items-center gap-3">
            <Button
              isLoading={
                isCancelPcInOutPermitByIdLoading ||
                isCancelWarehouseInOutPermitByIdLoading ||
                isCancelPermitToWorkByIdLoading ||
                isCancelLostStickerIDCardRequestLoading ||
                isCancelParkingEntryStickerLoading ||
                isCancelOfficeAccessAuthorizationLoading ||
                isCancelIssuanceRenewalIdForStaffFromContractedCompaniesLoading
              }
              onClick={handleCancelPermit}
            >
              {t("yesCancel")}
            </Button>
            <Button
              onClick={() => onOpenChange()}
              className="bg-transparent border-2 border-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("noClose")}
            </Button>
          </div>
        </div>
      </ModalWrapper>
    </>
  );
};

export default memo(CancelPermitModal);
